import classNames from '@ui-components-3/ui/lib/utils/classNames'
import VisitTypes from 'constants/visit-types'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import Tooltip from '@ui-components-3/ui/lib/components/Tooltip'

type ClaimProps = {
  onClaim: () => void
  visibleOnHover?: boolean
  isSuccess: boolean
  variant?: string
  hasQueuedCrisisVisit?: boolean
  hasInProgressVisit?: boolean
}

const Claim = ({
  onClaim,
  visibleOnHover,
  isSuccess,
  variant,
  hasQueuedCrisisVisit,
  hasInProgressVisit,
}: ClaimProps) => {
  const [claimStage, setClaimStage] = useState(0)
  const [isClaiming, setIsClaiming] = useState(false)

  const onConfirmClaim = useCallback(async () => {
    try {
      setIsClaiming(true)
      await onClaim()
    } catch (error) {
      console.log(error)
      setIsClaiming(false)
      toast.error(`Failed to claim ${isSuccess ? 'session' : 'visit'}`)
    }
  }, [isSuccess, onClaim])

  const isCrisis = variant === VisitTypes.crisis
  const isClaimDisabledBecauseOfCrisis = !isCrisis && !!hasQueuedCrisisVisit

  const memberLabel = useMemo(() => {
    if (isCrisis) return 'Caller'

    return isSuccess ? 'Student' : 'Patient'
  }, [isCrisis, isSuccess])

  const isClaimDisabled = isClaimDisabledBecauseOfCrisis || hasInProgressVisit

  const disabledTooltipContent = useMemo(() => {
    if (isClaimDisabledBecauseOfCrisis) return 'Claim the Crisis Line Caller in your queue first'

    if (hasInProgressVisit) return 'You already have an active visit'

    return null
  }, [hasInProgressVisit, isClaimDisabledBecauseOfCrisis])

  const claimButton = useMemo(
    () => (
      <button
        type="button"
        data-visible-on-hover={visibleOnHover ? '' : undefined}
        onClick={() => setClaimStage(1)}
        className={classNames(
          'btn-small',
          isCrisis ? 'btn-neutral' : 'btn-primary',
          isClaimDisabled && 'cursor-not-allowed',
        )}
        disabled={isClaimDisabled}
      >
        Claim {memberLabel}
      </button>
    ),
    [isClaimDisabled, isCrisis, memberLabel, visibleOnHover],
  )

  const content = useMemo(() => {
    if (claimStage) {
      return (
        <>
          <button
            type="button"
            className="btn-small btn-fuchsia-outlined"
            onClick={() => setClaimStage(0)}
            disabled={!!isClaiming}
          >
            Cancel claim
          </button>
          <button
            type="button"
            className={classNames('btn-small', isCrisis ? 'btn-neutral-outlined' : 'btn-primary-outlined')}
            onClick={onConfirmClaim}
            disabled={!!isClaiming}
          >
            Confirm claim
          </button>
        </>
      )
    }

    if (disabledTooltipContent) {
      return (
        <Tooltip content={disabledTooltipContent} placement="top">
          {claimButton}
        </Tooltip>
      )
    }

    return claimButton
  }, [claimButton, claimStage, disabledTooltipContent, isClaiming, isCrisis, onConfirmClaim])

  return <div className="flex justify-end items-center gap-4">{content}</div>
}

export default Claim
